<template>
  <b-row :class="`mx-0 ${rowClass}`">
    <b-col v-if="title" cols="12" class="padding-0">
      <div class="font-size-12 color-complementary-300 margin-bottom-8">
        {{ title }}
        <span v-if="infoTooltip" v-b-tooltip.hover :title="infoTooltip">
          <mdicon name="help-circle-outline" size="16" />
        </span>
      </div>
    </b-col>
    <b-col
      cols="12"
      class="padding-0 dropdown"
      :class="{ 'dropdown--disabled': disabled }"
      ref="drop-up"
    >
      <div class="position-relative" @mouseleave="show = false">
        <div
          v-if="!show || checkScreenDevice(['mobile', 'tablet'])"
          class="dropdown__static"
          :class="{
            'dropdown__static--multiple': multiple,
            'is-invalid': generateState === false,
            'padding-left-40': !hasRemoveIcon,
          }"
          @click="showToggle"
        >
          <div v-if="inputValueCaption" class="dropdown__text">
            {{ inputValueCaption }}
          </div>
          <div v-if="!hasData" class="dropdown__placeholder">
            {{ this.placeholder }}
          </div>
          <div class="dropdown__left-icon">
            <span
              v-if="
                hasRemoveIcon &&
                hasData &&
                !disabled &&
                checkScreenDevice(['desktop'])
              "
              @click="resetValue"
              class="dropdown__reset"
            >
              <mdicon name="close" size="24" />
            </span>
            <span v-if="loading && checkScreenDevice(['desktop'])" class="flex">
              <mdicon name="loading" size="20" class="spinner" />
            </span>
            <span
              v-if="
                showArrowIcon &&
                (!loading || checkScreenDevice(['mobile', 'tablet']))
              "
            >
              <mdicon name="chevron-down" size="24" />
            </span>
          </div>
        </div>
        <b-form-input
          v-if="show && checkScreenDevice(['desktop'])"
          v-model="searchText"
          :placeholder="inputPlaceholder"
          autocomplete="off"
          :class="{ 'padding-left-40': !hasRemoveIcon }"
          trim
          autofocus
          @click="showToggle"
        />
        <span
          v-if="show && checkScreenDevice(['desktop'])"
          class="dropdown__left-icon flex"
          @click="showToggle"
        >
          <span
            v-if="searchText"
            @click.stop="searchText = null"
            class="dropdown__reset"
          >
            <mdicon name="close" size="24" />
          </span>
          <span v-if="loading" class="flex">
            <mdicon name="loading" size="20" class="spinner" />
          </span>
          <span v-if="!loading">
            <mdicon name="chevron-up" size="24" />
          </span>
        </span>
        <collapse-transition>
          <div
            v-if="show"
            class="position-absolute w-100 z-index-2 mobile-none tablet-none"
            :class="{ 'drop-up': dropUp }"
          >
            <div
              class="dropdown__options"
              v-if="filteredOptions.length && !loading"
            >
              <div
                v-for="(option, index) in filteredOptions"
                :key="index"
                @click.stop="selectOption(option)"
                class="dropdown__option"
                :class="{
                  'dropdown__option--selected': isSelected(option) && !multiple,
                }"
              >
                <b-checkbox
                  v-if="multiple"
                  class="dropdown__checkbox"
                  :checked="isSelected(option)"
                  @change.native="selectOption(option)"
                />
                <span class="dropdown__option-text">{{ option.caption }}</span>
              </div>
            </div>
            <div
              v-if="!filteredOptions.length && !loading"
              class="dropdown__options"
            >
              <div
                v-if="isEditable"
                class="dropdown__create-item"
                @click="saveEnum"
              >
                <span class="dropdown__option-icon">
                  <mdicon name="plus" size="24" />
                </span>
                <span class="dropdown__option-text">
                  {{ `${$lang.make} "${searchText}"` }}
                </span>
              </div>
              <div v-else class="dropdown__empty">
                {{ $lang.emptyDropdown }}
              </div>
            </div>
            <div v-if="loading" class="dropdown__options">
              <div class="dropdown__empty">{{ $lang.updating_data }}</div>
            </div>
          </div>
        </collapse-transition>
        <MobilePopup :show="show" @setShow="showToggle">
          <template>
            <div
              v-if="filteredOptions.length > 0"
              class="position-sticky top-0 background-surface padding-bottom-8"
            >
              <b-form-input
                v-model="searchText"
                :placeholder="inputPlaceholder"
                autocomplete="off"
                :class="{ 'padding-left-40': !hasRemoveIcon }"
                trim
                autofocus
              />
              <span class="dropdown__left-icon flex">
                <span
                  v-if="searchText"
                  @click.stop="searchText = null"
                  class="dropdown__reset"
                >
                  <mdicon name="close" size="24" />
                </span>
                <span v-if="loading" class="flex">
                  <mdicon name="loading" size="20" class="spinner" />
                </span>
              </span>
            </div>
            <div
              class="dropdown__options"
              v-if="filteredOptions.length && !loading"
            >
              <div
                v-for="(option, index) in filteredOptions"
                :key="index"
                @click.stop="selectOption(option)"
                class="dropdown__option"
                :class="{
                  'dropdown__option--selected': isSelected(option) && !multiple,
                }"
              >
                <b-checkbox
                  v-if="multiple"
                  class="dropdown__checkbox"
                  :checked="isSelected(option)"
                />
                <span class="dropdown__option-text">{{ option.caption }}</span>
              </div>
            </div>
            <div
              v-if="!filteredOptions.length && !loading"
              class="dropdown__options"
            >
              <div
                v-if="isEditable"
                class="dropdown__create-item"
                @click="saveEnum"
              >
                <span class="dropdown__option-icon">
                  <mdicon name="plus" size="24" />
                </span>
                <span class="dropdown__option-text">
                  {{ `${$lang.make} "${searchText}"` }}
                </span>
              </div>
              <div v-else class="dropdown__empty">
                {{ $lang.emptyDropdown }}
              </div>
            </div>
            <div v-if="loading" class="dropdown__options">
              <div class="dropdown__empty">{{ $lang.updating_data }}</div>
            </div>
          </template>
        </MobilePopup>
      </div>
    </b-col>
    <b-col
      v-if="multiple && inputValue.length"
      cols="12"
      class="padding-0 margin-top-16 dropdown__multiple"
    >
      <div
        class="dropdown__multiple-item"
        v-for="(item, index) in inputValue"
        :key="index"
      >
        <span class="dropdown__multiple-text">{{ item.caption }}</span>
        <span class="dropdown__multiple-icon" @click.stop="removeOption(item)">
          <mdicon name="close" size="16" />
        </span>
      </div>
    </b-col>
    <b-col cols="12" class="padding-0">
      <FeedbackMessages
        :state="generateState"
        :label="label"
        :vuelidateObj="vuelidateObj"
      />
    </b-col>
  </b-row>
</template>

<script>
import publicMixin from "@/mixin/publicMixin.vue";
import FeedbackMessages from "../feedback-messages/FeedbackMessages.vue";
import MobilePopup from "../../modal/MobilePopup.vue";
import { includes, pathOr } from "ramda";
import { addEnumItem } from "@/services";
import {
  checkIsEditableEnum,
  renderEumOptions,
  checkScreenDevice,
} from "@/shared/utils";

export default {
  mixins: [publicMixin],
  components: { FeedbackMessages, MobilePopup },
  props: {
    value: {}, // watch model
    name: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "عنوان",
    },
    id: {
      type: String,
      default: "default-id",
    },
    type: {
      type: String,
      default: "text",
    },
    placeholder: {
      type: String,
      default: "",
    },
    vuelidateObj: {
      type: Object,
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    hasRemoveIcon: {
      type: Boolean,
      default: true,
    },
    inputPlaceholder: {
      type: String,
      default: "جستجو...",
    },
    rowClass: {
      type: String,
      default: "margin-bottom-22",
    },
    infoTooltip: {
      type: String,
      default: "",
    },
    categoryName: {
      type: String,
      default: "",
    },
    parentId: {
      type: Number,
      default: 0,
    },
    parentOptions: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showArrowIcon: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      show: false,
      searchText: null,
      loading: false,
      options: [],
    };
  },
  computed: {
    inputValue: {
      /* guidance: watching input value */
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", this.name, val);
      },
    },
    filteredOptions() {
      this.generateOptions();
      const options = this.options.map((option) => {
        return {
          ...option,
          selected: option.id === pathOr(null, ["id"], this.inputValue),
        };
      });

      return this.searchText
        ? options.filter((option) =>
            includes(
              this.searchText.toLowerCase(),
              option.caption.toLowerCase()
            )
          )
        : options;
    },
    inputValueCaption() {
      return pathOr("", ["caption"], this.inputValue);
    },
    hasData() {
      return !!(this.multiple ? this.inputValue.length : this.inputValue);
    },
    generateState() {
      return this.vuelidateObj && this.vuelidateObj.$dirty
        ? !this.vuelidateObj.$error
        : null;
    },
    isEditable() {
      return checkIsEditableEnum(this.categoryName);
    },
  },
  watch: {
    categoryName: {
      immediate: true,
      handler(value) {
        if (value) {
          this.generateOptions();
        }
      },
    },
  },
  methods: {
    includes,
    pathOr,
    checkIsEditableEnum,
    renderEumOptions,
    checkScreenDevice,
    showToggle() {
      if (this.disabled) return;
      this.show = !this.show;
      this.searchText = null;
      this.$emit("focus", this.name);
    },
    resetValue() {
      this.inputValue = this.multiple ? [] : null;
    },
    selectOption(option) {
      if (!this.multiple && option.selected) return;
      const alreadySelected = this.multiple
        ? this.inputValue.some((val) => pathOr(null, ["id"], val) === option.id)
        : pathOr(null, ["id"], this.inputValue) === option.id;

      if (alreadySelected) {
        this.removeOption(option);
      } else {
        this.inputValue = this.multiple
          ? [...this.inputValue, { ...option }]
          : { ...option };
      }
      this.show = this.multiple;
      this.searchText = this.multiple ? this.searchText : null;
      this.$emit("change", option);
    },
    removeOption(item) {
      this.inputValue = this.multiple
        ? [...this.inputValue.filter((val) => val.id !== item.id)]
        : null;
    },
    isSelected(option) {
      if (this.multiple) {
        return this.inputValue.some((item) => item.id === option.id);
      } else {
        return pathOr(false, ["selected"], option);
      }
    },
    blur() {
      this.show = false;
      this.$emit("blur", this.name, this.inputValue);
    },
    generateOptions() {
      // get tag options of enumeration data
      this.options = this.parentOptions.length
        ? this.parentOptions
        : renderEumOptions(this.categoryName, true);
      this.$emit("getOptions", this.options);

      // set default value by parent value when mode is edit or set by is default option
      const defaultItem = this.options.find((item) => item.is_default);
      const defaultValue = this.multiple ? [defaultItem] : defaultItem;
      this.inputValue = this.inputValue || defaultValue;
    },
    async saveEnum() {
      try {
        this.loading = true;
        const response = await addEnumItem(
          this.searchText,
          this.categoryName,
          this.parentId
        );
        const { message } = response;
        this.generateOptions();
        this.searchText = null;
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
